import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Dumbbell, Calendar, Zap, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function TemplateDialog({ open, onOpenChange, onPlanCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);

  useEffect(() => {
    if (open) loadTemplates();
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/training-plans/templates`, { headers: getHeaders() });
      setTemplates(res.data.templates || []);
    } catch { toast.error("Templates konnten nicht geladen werden"); }
    setLoading(false);
  };

  const handleUseTemplate = async (templateId) => {
    setCreating(templateId);
    try {
      const res = await axios.post(`${API}/training-plans/from-template`, { template_id: templateId }, { headers: getHeaders() });
      toast.success(`Plan "${res.data.name}" erstellt`);
      onOpenChange(false);
      if (onPlanCreated) onPlanCreated(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Erstellen");
    }
    setCreating(null);
  };

  const handleDeleteCustom = async (e, templateId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/training-plans/templates/${templateId}`, { headers: getHeaders() });
      toast.success("Vorlage gelöscht");
      loadTemplates();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const catColors = {
    hypertrophie: "bg-violet-100 text-violet-700",
    kraft: "bg-red-100 text-red-700",
    ausdauer: "bg-sky-100 text-sky-700",
    hyrox: "bg-orange-100 text-orange-700",
    ems: "bg-yellow-100 text-yellow-700",
    reha: "bg-teal-100 text-teal-700",
    pilates: "bg-pink-100 text-pink-700",
    yoga: "bg-indigo-100 text-indigo-700",
  };

  const customTemplates = templates.filter(t => t.custom);
  const builtinTemplates = templates.filter(t => !t.custom);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Vorlagen-Bibliothek
          </DialogTitle>
          <DialogDescription>Professionelle Trainingspläne mit 1 Klick erstellen</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Lade Vorlagen...</div>
          ) : (
            <>
              {customTemplates.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">Meine Vorlagen</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {customTemplates.map(t => (
                      <div key={t.id} className="border border-amber-200 rounded-xl p-4 hover:shadow-md transition-all bg-amber-50/30" data-testid={`template-${t.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{t.icon}</span>
                            <div>
                              <h3 className="text-sm font-bold text-slate-900">{t.name}</h3>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${catColors[t.category] || "bg-slate-100 text-slate-600"}`}>
                                {t.category}
                              </span>
                            </div>
                          </div>
                          <button onClick={(e) => handleDeleteCustom(e, t.id)} className="p-1 rounded hover:bg-red-100 text-red-400" title="Vorlage löschen" data-testid={`delete-template-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {t.description && <p className="text-xs text-slate-500 mb-3">{t.description}</p>}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.day_count} Tage</span>
                            <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" /> {t.exercise_count} Übungen</span>
                          </div>
                          <Button size="sm" onClick={() => handleUseTemplate(t.id)} disabled={creating === t.id} className="text-xs" data-testid={`use-template-${t.id}`}>
                            {creating === t.id ? "Erstelle..." : "Verwenden"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                {customTemplates.length > 0 && (
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Standard-Vorlagen</h3>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  {builtinTemplates.map(t => (
                    <div key={t.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-slate-300 transition-all" data-testid={`template-${t.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{t.icon}</span>
                          <div>
                            <h3 className="text-sm font-bold text-slate-900">{t.name}</h3>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${catColors[t.category] || "bg-slate-100 text-slate-600"}`}>
                              {t.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mb-3 leading-relaxed">{t.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.day_count} Tage</span>
                          <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" /> {t.exercise_count} Übungen</span>
                        </div>
                        <Button size="sm" onClick={() => handleUseTemplate(t.id)} disabled={creating === t.id} className="text-xs" data-testid={`use-template-${t.id}`}>
                          {creating === t.id ? "Erstelle..." : "Verwenden"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
